import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { getNumberParam } from '@/utils/urlParams';

/**
 * Custom hook to dynamically set container max-width based on URL query parameter.
 *
 * Usage: Add ?maxwidth=2000 to the URL to override the default max-width.
 *
 * This automatically calculates and updates both:
 * - --container-max-width (the container's max width in pixels)
 * - --clamp (the root font-size clamp value for ultra-wide screens)
 *
 * Formula: clamp = 8 × containerMaxWidth / 1440
 *
 * Example: ?maxwidth=2000
 * - Sets --container-max-width: 2000px
 * - Calculates --clamp: 8 × 2000 / 1440 = 11.11px
 */
export function useDynamicMaxWidth() {
  const router = useRouter();

  useEffect(() => {
    // Default values from CSS
    const DEFAULT_MAX_WIDTH = 1600;
    const BASE = 8;
    const SIZE = 1440;

    // Get the maxwidth param from URL
    const maxWidthParam = getNumberParam('maxwidth', DEFAULT_MAX_WIDTH);

    // Only update if different from default
    if (maxWidthParam !== DEFAULT_MAX_WIDTH) {
      // Calculate the new clamp value using the formula:
      // clamp = --base × --container-max-width / --size
      const newClamp = (BASE * maxWidthParam) / SIZE;

      // Update CSS variables on the root element
      document.documentElement.style.setProperty('--container-max-width', `${maxWidthParam}px`);
      document.documentElement.style.setProperty('--clamp', `${newClamp}px`);

      // console.log(`%cDynamic max-width applied:`, 'color: goldenrod; font-size: 16px;', {
      //   containerMaxWidth: `${maxWidthParam}px`,
      //   clamp: `${newClamp.toFixed(2)}px`,
      //   formula: `${BASE} × ${maxWidthParam} / ${SIZE}`,
      // });
    } else {
      // Reset to defaults if no param or default value
      document.documentElement.style.removeProperty('--container-max-width');
      document.documentElement.style.removeProperty('--clamp');
    }
  }, [router.asPath]); // Re-run when route changes
}
